import { GridOptions, RowNumbersOptions } from "ag-grid-community";
import { useMemo } from "react";

import { PAGINATION_PAGE_SIZES } from "../constants";
import { BaseTableData } from "../types";

import LoadingSpinner from "@/components/LoadingSpinner";

const getRowSelectionMode = (
  enableRowSelection: boolean | undefined,
  enableMultiRowSelection: boolean | undefined,
): "multiple" | "single" | undefined => {
  if (!enableRowSelection) {
    return undefined;
  }

  if (enableMultiRowSelection) {
    return "multiple";
  }

  return "single";
};

interface UseGridOptionsProps<T extends BaseTableData> {
  hoverable: boolean;
  enablePagination?: boolean;
  pageSize: number;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  rowNumbers?: boolean | RowNumbersOptions;
  emptyStateMessage: string;
  customGridOptions?: Partial<GridOptions<T>>;
}

export const useGridOptions = <T extends BaseTableData>({
  hoverable,
  enablePagination,
  pageSize,
  enableRowSelection,
  enableMultiRowSelection,
  rowNumbers,
  emptyStateMessage,
  customGridOptions,
}: UseGridOptionsProps<T>) => {
  const gridOptions = useMemo<GridOptions<T>>(
    () => ({
      enableCellTextSelection: true,
      suppressRowHoverHighlight: !hoverable,
      pagination: enablePagination,
      paginationPageSize: pageSize,
      paginationPageSizeSelector: enablePagination ? PAGINATION_PAGE_SIZES : false,
      domLayout: "normal",
      rowSelection: getRowSelectionMode(enableRowSelection, enableMultiRowSelection),
      suppressMoveWhenRowDragging: true,
      suppressScrollOnNewData: true,
      suppressAnimationFrame: false,
      suppressPropertyNamesCheck: true,
      rowNumbers: rowNumbers,
      loadingOverlayComponent: () => (
        <div className="flex h-full items-center justify-center p-4">
          <div className="text-sm text-gray-500">
            <LoadingSpinner size={4} />
          </div>
        </div>
      ),
      noRowsOverlayComponent: () => (
        <div
          className="flex h-full items-center justify-center p-8"
          data-entity-preview-empty-state="table"
        >
          <div className="text-sm text-gray-500">{emptyStateMessage}</div>
        </div>
      ),
      ...customGridOptions,
    }),
    [
      hoverable,
      enablePagination,
      pageSize,
      enableRowSelection,
      enableMultiRowSelection,
      rowNumbers,
      emptyStateMessage,
      customGridOptions,
    ],
  );

  return { gridOptions };
};
